/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import ActivityLogIcon from "../icons/ActivityLog";
import CompaniesIcon from "../icons/Companies";
import DevicesIcon from "../icons/Devices";
import HomeIcon from "../icons/Home";
import MyAccountIcon from "../icons/MyAccount";
import SiteIcon from "../icons/Site";
import TransactionsIcon from "../icons/Transactions";
import UsersIcon from "../icons/Users";
import { ROLE } from "../utils/constants";


export const useSidebar = (role: string, company_id: string) => {
  const { t } = useTranslation();
  const location = useLocation();

  const path = location.pathname;

  const financeSideBarItems = useCallback(
    () => [
      {
        label: t("sidebar.home"),
        link: `/dashboard/finance/home/${company_id}`,
        icon: HomeIcon,
      },
      {
        label: t("sidebar.sites"),
        link: `/dashboard/finance/sites/${company_id}?isFromFinance=true`,
        icon: SiteIcon,
      },
      {
        label: t("sidebar.transactions"),
        link: `/dashboard/finance/transactions/${company_id}`,
        icon: TransactionsIcon,
      },
      {
        label: t("sidebar.activity_log"),
        link: "/dashboard/my-account",
        icon: MyAccountIcon,
      },
    ],
    [company_id, t]
  );

  const operationsSideBarItems = useCallback(() => [
    {
      label: t("sidebar.home"),
      link: `/dashboard/operations/home/${company_id}`,
      icon: HomeIcon,
    },
    {
      label: t("sidebar.sites"),
      link: `/dashboard/operations/sites/${company_id}`,
      icon: SiteIcon,
    },
    {
      label: t("sidebar.activity_log"),
      link: `/dashboard/operations/activity-log/${company_id}`,
      icon: ActivityLogIcon,
    },
    {
      label: t("sidebar.my_account"),
      link: "/dashboard/my-account",
      icon: MyAccountIcon,
    },
  ], [company_id, t]);

  const managerSideBarItems = useCallback(() => [
    {
      label: t("sidebar.home"),
      link: `/dashboard/senior-manager/home/${company_id}`,
      icon: HomeIcon,
    },
    {
      label: t("sidebar.sites"),
      link: `/dashboard/senior-manager/sites/${company_id}`,
      icon: SiteIcon,
    },
    {
      label: t("sidebar.my_account"),
      link: `/dashboard/my-account`,
      icon: MyAccountIcon,
    },
  ], [company_id, t]);

  const adminSideBarItems = useCallback(
    () => [
      {
        label: t("sidebar.home"),
        link: `/dashboard/admin/home/${company_id}`,
        icon: HomeIcon,
      },
      {
        label: t("sidebar.companies"),
        link: `/dashboard/admin/companies/${company_id}`,
        icon: CompaniesIcon,
      },
      {
        label: t("sidebar.user_analytics"),
        link: `/dashboard/admin/user-analytics/${company_id}`,
        icon: UsersIcon,
      },
      {
        label: t("devices"),
        link: `/dashboard/admin/devices/${company_id}`,
        icon: DevicesIcon,
      },
      {
        label: t("sidebar.my_account"),
        link: "/dashboard/my-account",
        icon: MyAccountIcon,
      },
    ],
    [company_id, t]
  );

const removeMyAccountForAdmin = useCallback((items: any[]) => {
  return items.filter((item) => item.label !== "My Account");
}, []);


  const getItemsByPath = useCallback(() => {
    if (path.includes("finance")) return financeSideBarItems();
    if (path.includes("operations")) return operationsSideBarItems();
    if (path.includes("senior-manager")) return managerSideBarItems();
    return adminSideBarItems();
  }, [path, financeSideBarItems, operationsSideBarItems, managerSideBarItems, adminSideBarItems]);

  return useMemo(() => {
    if (role === ROLE.ADMIN) {
      if (path.includes("admin")) return adminSideBarItems();
      return removeMyAccountForAdmin(getItemsByPath());
    }

    if (role === ROLE.MANAGER) {
      if (!path.includes("finance") && !path.includes("operations")) {
        return managerSideBarItems();
      }
      return removeMyAccountForAdmin(getItemsByPath());
    }

    if (role === ROLE.FINANCE) return financeSideBarItems();
    if (role === ROLE.OPERATIONS) return operationsSideBarItems();

    return [];
  }, [
    role,
    path,
  
    adminSideBarItems,
    operationsSideBarItems,
    financeSideBarItems,
    managerSideBarItems,
    removeMyAccountForAdmin,
    getItemsByPath,
  ]);
};
